<template>
	<div class="remember-me-wrapper">
		<!-- Левая часть: чекбокс + "Запомнить меня" -->
		<label class="remember-left-group">
			<div class="checkbox-container">
				<input
					type="checkbox"
					class="checkbox-input"
					:checked="modelValue"
					@change="onToggle($event.target.checked)"
					:aria-label="rememberLabel"
				/>
				<div class="checkbox-box">
					<svg class="checkmark" viewBox="0 0 24 24">
						<path d="M5 12l5 5L20 7" />
					</svg>
				</div>
			</div>

			<span class="remember-text">
				{{ rememberLabel }}
			</span>

			<button
				v-if="savedLogins.length"
				type="button"
				class="dropdown-toggle"
				@click.stop="toggleDropdown"
				:title="showDropdown ? 'Скрыть' : 'Показать сохранённые логины'"
			>
				<svg class="dropdown-icon" :class="{ 'rotate': showDropdown }" viewBox="0 0 24 24">
					<path d="M7 10l5 5 5-5" />
				</svg>
			</button>
		</label>

		<!-- Правая часть: "Забыли пароль?" -->
		<button 
			type="button" 
			class="forgot-link"
			@click="$emit('forgot-password')"
		>
			{{ forgotLabel }}
		</button>

		<!-- Выпадающий список логинов -->
		<transition name="dropdown">
			<div
				v-if="showDropdown && savedLogins.length"
				class="remember-dropdown"
			>
				<div
					v-for="(login, index) in savedLogins"
					:key="login"
					class="dropdown-item"
					:style="{ animationDelay: `${index * 30}ms` }"
					@click="selectLogin(login)"
				>
					<svg class="item-icon" viewBox="0 0 24 24">
						<path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
						<circle cx="12" cy="7" r="4" />
					</svg>
					<span class="item-text">{{ login }}</span>
					<svg class="item-arrow" viewBox="0 0 24 24">
						<path d="M5 12h14M12 5l7 7-7 7" />
					</svg>
				</div>
			</div>
		</transition>
	</div>
</template>

<script>
const STORAGE_KEY = 'remembered_logins'

export default {
	name: 'RememberMe',
	props: {
		modelValue: {
			type: Boolean,
			default: false,
		},
		currentLogin: {
			type: String,
			default: '',
		},
		rememberLabel: {
			type: String,
			required: true,
		},
		forgotLabel: {
			type: String,
			required: true,
		},
	},
	emits: ['update:modelValue', 'select-login', 'forgot-password'],
	data() {
		return {
			savedLogins: [],
			showDropdown: false,
		}
	},
	mounted() {
		this.loadLogins()
		document.addEventListener('click', this.handleClickOutside)
	},
	beforeUnmount() {
		document.removeEventListener('click', this.handleClickOutside)
	},
	methods: {
		loadLogins() {
			try {
				const raw = localStorage.getItem(STORAGE_KEY)
				const parsed = raw ? JSON.parse(raw) : []
				if (Array.isArray(parsed)) {
					this.savedLogins = parsed.filter((v) => typeof v === 'string')
				}
			} catch {
				this.savedLogins = []
			}
		},
		saveLogins() {
			try {
				localStorage.setItem(STORAGE_KEY, JSON.stringify(this.savedLogins))
			} catch {
				/* ignore */
			}
		},
		onToggle(checked) {
			this.$emit('update:modelValue', checked)
			const login = this.currentLogin.trim()
			if (checked && login) this.addLogin(login)
		},
		addLogin(login) {
			if (!this.savedLogins.includes(login)) {
				this.savedLogins.unshift(login)
				if (this.savedLogins.length > 5) {
					this.savedLogins = this.savedLogins.slice(0, 5)
				}
				this.saveLogins()
			}
		},
		toggleDropdown() {
			this.showDropdown = !this.showDropdown
		},
		selectLogin(login) {
			this.showDropdown = false
			this.$emit('select-login', login)
		},
		handleClickOutside(e) {
			if (!this.$el.contains(e.target)) {
				this.showDropdown = false
			}
		},
	},
}
</script>

<style scoped>
/* Основной контейнер */
.remember-me-wrapper {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 1rem;
	max-width: 450px;
	width: 100%;
	position: relative;
	padding: 0.5rem 0;
	flex-wrap: wrap;
}

/* Левая группа */
.remember-left-group {
  margin-top: 20px;
	display: flex;
	align-items: center;
	gap: 0.625rem;
	cursor: pointer;
	position: relative;
	user-select: none;
}

/* Контейнер чекбокса */
.checkbox-container {
	position: relative;
	width: 22px;
	height: 22px;
	flex-shrink: 0;
}

/* Скрытый input */
.checkbox-input {
	position: absolute;
	inset: 0;
	opacity: 0;
	cursor: pointer;
	margin: 0;
	z-index: 2;
}

/* Кастомный чекбокс */
.checkbox-box {
	position: absolute;
	inset: 0;
	width: 22px;
	height: 22px;
	border-radius: 7px;
	border: 2px solid #cbd5e1;
	background: linear-gradient(145deg, #ffffff 0%, #f8fafc 100%);
	box-shadow:
		0 2px 4px rgba(15, 23, 42, 0.06),
		inset 0 1px 2px rgba(255, 255, 255, 0.8);
	display: flex;
	align-items: center;
	justify-content: center;
	transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
	overflow: visible;
}

/* Волна при клике */
.checkbox-box::before {
	content: '';
	position: absolute;
	inset: -12px;
	border-radius: 50%;
	background: radial-gradient(
		circle,
		rgba(59, 130, 246, 0.4) 0%,
		rgba(37, 99, 235, 0.25) 35%,
		rgba(59, 130, 246, 0.1) 60%,
		transparent 80%
	);
	opacity: 0;
	transform: scale(0.5);
	pointer-events: none;
	transition: none;
}

/* SVG галочка */
.checkmark {
	width: 14px;
	height: 14px;
	stroke: #ffffff;
	stroke-width: 3;
	stroke-linecap: round;
	stroke-linejoin: round;
	fill: none;
	transform: scale(0) rotate(-12deg);
	opacity: 0;
	filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.3));
	transition: all 0.35s cubic-bezier(0.68, -0.55, 0.265, 1.55);
}

/* Hover эффект */
.remember-left-group:hover .checkbox-box {
	border-color: #94a3b8;
	transform: translateY(-1px);
	box-shadow:
		0 4px 8px rgba(15, 23, 42, 0.1),
		inset 0 1px 2px rgba(255, 255, 255, 0.8);
}

/* Focus */
.checkbox-input:focus-visible ~ .checkbox-box {
	border-color: #3b82f6;
	box-shadow:
		0 0 0 4px rgba(59, 130, 246, 0.15),
		0 2px 6px rgba(15, 23, 42, 0.12);
}

/* Active (нажатие) */
.checkbox-input:active ~ .checkbox-box {
	transform: scale(0.94);
}

/* CHECKED состояние */
.checkbox-input:checked ~ .checkbox-box {
	border-color: #3b82f6;
	background: linear-gradient(145deg, #3b82f6 0%, #2563eb 100%);
	box-shadow:
		0 4px 12px rgba(59, 130, 246, 0.4),
		0 2px 6px rgba(37, 99, 235, 0.3),
		inset 0 1px 2px rgba(255, 255, 255, 0.25);
	transform: scale(1.05);
}

/* Галочка появляется */
.checkbox-input:checked ~ .checkbox-box .checkmark {
	transform: scale(1) rotate(0deg);
	opacity: 1;
	animation: checkmark-draw 0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55);
}

/* Волна при включении */
.checkbox-input:checked ~ .checkbox-box::before {
	animation: ripple-wave 0.7s cubic-bezier(0.25, 0.46, 0.45, 0.94);
}

/* Анимация волны */
@keyframes ripple-wave {
	0% {
		opacity: 0;
		transform: scale(0.5) rotate(0deg);
	}
	20% {
		opacity: 0.8;
		transform: scale(0.8) rotate(45deg);
	}
	40% {
		opacity: 0.6;
		transform: scale(1.2) rotate(90deg);
	}
	60% {
		opacity: 0.4;
		transform: scale(1.8) rotate(180deg);
	}
	80% {
		opacity: 0.15;
		transform: scale(2.5) rotate(270deg);
	}
	100% {
		opacity: 0;
		transform: scale(3.2) rotate(360deg);
	}
}

/* Анимация появления галочки */
@keyframes checkmark-draw {
	0% {
		transform: scale(0) rotate(-12deg);
		opacity: 0;
	}
	50% {
		transform: scale(1.15) rotate(3deg);
		opacity: 1;
	}
	100% {
		transform: scale(1) rotate(0deg);
		opacity: 1;
	}
}

/* Текст */
.remember-text {
  /* margin-top: 20px; */
  font-family: 'Poppins', sans-serif;
	font-size: 15px;
	line-height: 1.4;
	color: #374151;
	font-weight: 500;
	white-space: nowrap;
	transition: color 0.2s ease;
}

.remember-left-group:hover .remember-text {
	color: #1f2937;
}

/* Кнопка dropdown */
.dropdown-toggle {
	display: flex;
	align-items: center;
	justify-content: center;
	width: 20px;
	height: 20px;
	margin-left: 0.25rem;
	padding: 0;
	border: none;
	background: transparent;
	cursor: pointer;
	border-radius: 4px;
	transition: all 0.2s ease;
}

.dropdown-toggle:hover {
	background-color: #f3f4f6;
}

.dropdown-icon {
	width: 14px;
	height: 14px;
	stroke: #6b7280;
	stroke-width: 2.5;
	stroke-linecap: round;
	stroke-linejoin: round;
	fill: none;
	transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.dropdown-icon.rotate {
	transform: rotate(180deg);
}

.dropdown-toggle:hover .dropdown-icon {
	stroke: #3b82f6;
}

/* Кнопка "Забыли пароль?" */
.forgot-link {
  margin-top: 20px;
  font-family: 'Poppins', sans-serif;
	font-size: 15px;
	margin-left: auto;
	padding: 0.375rem 0.625rem;
	font-weight: 500;
	color: #3b82f6;
	background: transparent;
	border: none;
	border-radius: 6px;
	cursor: pointer;
	white-space: nowrap;
	transition: all 0.2s ease;
}

.forgot-link:hover {
	color: #2563eb;
	background-color: #eff6ff;
	transform: translateX(-2px);
}

.forgot-link:active {
	transform: translateX(-2px) scale(0.97);
}

/* Выпадающий список */
.remember-dropdown {
	position: absolute;
	left: 0;
	top: calc(100% + 0.5rem);
	min-width: 240px;
	padding: 0.5rem;
	background: #ffffff;
	border: 1px solid #e5e7eb;
	border-radius: 12px;
	box-shadow:
		0 10px 25px rgba(0, 0, 0, 0.1),
		0 4px 10px rgba(0, 0, 0, 0.05);
	z-index: 50;
	overflow: hidden;
}

/* Элемент списка */
.dropdown-item {
	display: flex;
	align-items: center;
	gap: 0.75rem;
	padding: 0.75rem 0.875rem;
	border-radius: 8px;
	cursor: pointer;
	transition: all 0.2s ease;
	animation: item-slide-in 0.3s cubic-bezier(0.34, 1.56, 0.64, 1) backwards;
}

@keyframes item-slide-in {
	from {
		opacity: 0;
		transform: translateY(-8px);
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
}

.dropdown-item:hover {
	background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
	transform: translateX(4px);
}

.item-icon {
	width: 18px;
	height: 18px;
	stroke: #6b7280;
	stroke-width: 2;
	stroke-linecap: round;
	stroke-linejoin: round;
	fill: none;
	flex-shrink: 0;
	transition: all 0.2s ease;
}

.dropdown-item:hover .item-icon {
	stroke: #3b82f6;
	transform: scale(1.1);
}

.item-text {
	flex: 1;
	font-size: 0.875rem;
	color: #374151;
	font-weight: 500;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	transition: color 0.2s ease;
}

.dropdown-item:hover .item-text {
	color: #1f2937;
}

.item-arrow {
	width: 16px;
	height: 16px;
	stroke: #9ca3af;
	stroke-width: 2;
	stroke-linecap: round;
	stroke-linejoin: round;
	fill: none;
	flex-shrink: 0;
	opacity: 0;
	transform: translateX(-4px);
	transition: all 0.2s ease;
}

.dropdown-item:hover .item-arrow {
	opacity: 1;
	transform: translateX(0);
	stroke: #3b82f6;
}

/* Анимация выпадающего меню */
.dropdown-enter-active {
	animation: dropdown-in 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.dropdown-leave-active {
	animation: dropdown-out 0.2s ease-in;
}

@keyframes dropdown-in {
	from {
		opacity: 0;
		transform: translateY(-12px) scale(0.96);
	}
	to {
		opacity: 1;
		transform: translateY(0) scale(1);
	}
}

@keyframes dropdown-out {
	from {
		opacity: 1;
		transform: translateY(0) scale(1);
	}
	to {
		opacity: 0;
		transform: translateY(-8px) scale(0.98);
	}
}

/* Адаптивность */
@media (max-width: 767px) {
	.remember-me-wrapper {
		flex-direction: column;
		align-items: flex-start;
		gap: 0.75rem;
	}
	.remember-left-group {
		margin-top: 10px;
	}
	.forgot-link {
		margin-top: 0;
		margin-left: 0;
		align-self: flex-end;
	}
	.remember-dropdown {
		width: 100%;
		min-width: unset;
	}
}
</style>